<template>
  <div
    id="location_targeting_details"
    class="col-12 col-md-6">
    <!-- LOCATIONS -->
    <div class="row">
      <div class="col-12">
        <div
          class="pane-header"
          :data-entity-title-type="$t('names.inventory')">
          <span class="icon"><icon-colored-location /></span>
          <span class="text">{{ capitalize($t('names.inventory', { n: 1 })) }}</span>
          <slot
            v-if="isSharedPlan"
            name="pane-header-right" />

          <div
            v-if="!isDisplayVideoLineItem"
            class="kpis">
            <div
              v-if="!isCreative && !isDeal && !isCreativeMediaOwnerApproval"
              class="kpi"
              :data-value="targetedSiteCount">
              <icon-site />
              <div class="number-and-text">
                <loader-small v-if="isAudienceInfoLoading" />
                <span v-else>
                  {{ targetedSiteCountFormatted }}
                </span>
                <h4>{{ capitalize($t('names.site', { n: 2 })) }}</h4>
              </div>
            </div>
            <div
              v-if="!isCreative && !isDeal && !isCreativeMediaOwnerApproval"
              class="kpi"
              :data-value="physicalScreenCount ?? targetedScreenCount">
              <icon-unit />
              <div class="number-and-text">
                <!-- Count for geofences targeting -->
                <template v-if="areGeofencesCreatedLessThan5Min">
                  <loader-small v-if="isAudienceWithGeofencesInfoLoading" />
                  <span v-else>
                    {{ targetedScreenCountFormatted }}
                  </span>
                </template>
                <!-- Count for regular targeting -->
                <template v-else>
                  <loader-small v-if="isAudienceInfoLoading" />
                  <span v-else>
                    {{ physicalScreenCountFormatted }}
                  </span>
                </template>
                <h4>
                  {{ capitalize($t('names.screen', { n: 2 })) }}
                  <div
                    v-if="physicalScreenCount !== targetedScreenCount"
                    v-tooltip.bottom="{ content: overriddenScreenCountTooltipContent }">
                    <span class="ml-2 badge flip-vertical">
                      <icon-info />
                    </span>
                  </div>
                </h4>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12">
        <div class="pane-content pad-top">
          <div class="row">
            <div class="col-12">
              <selected-locations-tree
                :creative-orientations="creativeOrientations"
                :excluded-creative-orientations="excludedCreativeOrientations"
                :creative-formats="creativeFormats"
                :excluded-creative-formats="excludedCreativeFormats"
                :iab-categories="iabCategories"
                :excluded-iab-categories="excludedIabCategories"
                :languages="languages"
                :locations="locations"
                :excluded-languages="excludedLanguages"
                :excluded-locations="excludedLocations"
                :excluded-unit-packs="excludedUnitPacks"
                :excluded-unit-tags="excludedUnitTags"
                :sites="sites"
                :excluded-sites="excludedSites"
                :units="units"
                :page-sizes="pageSizes"
                :page-numbers="pageNumbers"
                :lengths="lengths"
                :excluded-units="excludedUnits"
                :excluded-geofence-groups="excludedGeofenceGroups"
                :geofence-groups="geofenceGroups"
                :has-access-to-all-targets="hasAccessToTargetedGeofenceGroups"
                :markets="markets"
                :excluded-markets="excludedMarkets"
                :media-types="mediaTypes"
                :networks="selectedNetworks"
                :unit-packs="unitPacks"
                :unit-tags="unitTags"
                :excluded-networks="excludedNetworks"
                :excluded-media-types="excludedMediaTypes"
                :is-unit-targeting-enabled="
                  organizationSettings && organizationSettings.uiEnableLineItemUnitTargeting
                " />
              <div
                v-if="
                  geofenceGroups.length > 0 &&
                  organizationSettings.uiEnableUpdatedInventoryExport &&
                  (organizationSettings.uiEnableLineItemSiteTargeting ||
                    organizationSettings.uiEnableLineItemUnitTargeting) &&
                  showExportSection &&
                  !isSharedPlan
                "
                class="inventory-in-geofences well">
                <h4>
                  <icon-geofence-group />
                  {{ capitalize($t('actions.exportInventoryFoundInGeofences')) }}
                </h4>
                <div class="inventory-in-geofences-actions">
                  <button
                    v-if="organizationSettings.uiEnableLineItemSiteTargeting || isSupplyOrganization"
                    name="downloadSite"
                    class="btn btn-primary btn-xxs mr-3"
                    @click="() => $emit('exportSites')">
                    <icon-download />
                    {{ capitalize($t('actions.downloadSites')) }}
                  </button>
                  <button
                    v-if="organizationSettings.uiEnableLineItemUnitTargeting || isSupplyOrganization"
                    name="downloadUnits"
                    class="btn btn-primary btn-xxs mr-3"
                    @click="() => $emit('exportUnits')">
                    <icon-download />
                    {{ capitalize($t('actions.downloadScreens')) }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template v-if="!isSharedPlan">
      <geofence-units-file-download
        ref="geofenceUnitsFileDownload"
        :name="entity.name"
        :units="unitGeofencesToExport" />
      <geofence-sites-file-download
        ref="geofenceSitesFileDownload"
        :name="entity.name"
        :sites="siteGeofencesToExport" />
    </template>
  </div>
</template>

<script>
  import { capitalize } from 'vue';
  import { mapGetters, mapState } from 'vuex';

  import { LoaderSmall, SelectedLocationsTree } from '@/common/components/controls/index';
  import { IconColoredLocation } from '@/common/icons/colored';
  import { IconDownload, IconGeofenceGroup, IconSite, IconUnit } from '@/common/icons/monochrome';
  import IconInfo from '@/common/icons/monochrome/Info.vue';
  import Creative from '@/common/models/creative/Creative';
  import InventoryTargeting from '@/common/models/InventoryTargeting';
  import LineItem from '@/common/models/lineitem/LineItem';
  import Package from '@/common/models/package/Package';
  import Plan from '@/common/models/plan/Plan';
  import { DASH } from '@/globals';
  import formatNumber from '@/utils/NumberFormatter';
  import i18n from '@/vueI18n';

  import GeofenceSitesFileDownload from './GeofenceSitesFileDownload';
  import GeofenceUnitsFileDownload from './GeofenceUnitsFileDownload';

  export default {
    components: {
      IconInfo,
      GeofenceUnitsFileDownload,
      GeofenceSitesFileDownload,
      IconColoredLocation,
      IconDownload,
      IconGeofenceGroup,
      IconSite,
      IconUnit,
      LoaderSmall,
      SelectedLocationsTree,
    },
    data() {
      return { DASH };
    },
    props: {
      entity: {
        type: Object,
      },
      excludedCreativeOrientations: {
        type: Array,
        default: () => [],
      },
      excludedCreativeFormats: {
        type: Array,
        default: () => [],
      },
      excludedIabCategories: {
        type: Array,
        default: () => [],
      },
      excludedLanguages: {
        type: Array,
        default: () => [],
      },
      excludedLocations: {
        type: Array,
        default: () => [],
      },
      excludedSites: {
        type: Array,
        default: () => [],
      },
      excludedUnits: {
        type: Array,
        default: () => [],
      },
      excludedMarkets: {
        type: Array,
        default: () => [],
      },
      excludedMediaTypes: {
        type: Array,
        default: () => [],
      },
      excludedNetworks: {
        type: Array,
        default: () => [],
      },
      excludedGeofenceGroups: {
        type: Array,
        default: () => [],
      },
      geofenceGroups: {
        type: Array,
        default: () => [],
      },
      hasAccessToTargetedGeofenceGroups: {
        type: Boolean,
        default: true,
      },
      creativeOrientations: {
        type: Array,
        default: () => [],
      },
      creativeFormats: {
        type: Array,
        default: () => [],
      },
      iabCategories: {
        type: Array,
        default: () => [],
      },
      languages: {
        type: Array,
        default: () => [],
      },
      locations: {
        type: Array,
        default: () => [],
      },
      markets: {
        type: Array,
        default: () => [],
      },
      mediaTypes: {
        type: Array,
        default: () => [],
      },
      selectedNetworks: {
        type: Array,
        default: () => [],
      },
      sites: {
        type: Array,
        default: () => [],
      },
      targetedUnitIDs: {
        type: Array,
        default: () => [],
      },
      targetedSiteIDs: {
        type: Array,
        default: () => [],
      },
      units: {
        type: Array,
        default: () => [],
      },
      unitPacks: {
        type: Array,
        default: () => [],
      },
      excludedUnitPacks: {
        type: Array,
        default: () => [],
      },
      unitTags: {
        type: Array,
        default: () => [],
      },
      excludedUnitTags: {
        type: Array,
        default: () => [],
      },
      unitGeofencesToExport: {
        type: Array,
        default: null,
      },
      physicalScreenCount: {
        type: Number,
        default: 0,
      },
      siteGeofencesToExport: {
        type: Array,
        default: null,
      },
      isAudienceInfoLoading: {
        type: Boolean,
        default: false,
      },
      isAudienceWithGeofencesInfoLoading: {
        type: Boolean,
        default: false,
      },
      // These next 3 variables will take the page size, page number and the total length of the object (units, sites, etc.) sent from the parent component
      // They're used to load the object in batches instead of loading everything at once.
      pageSizes: {
        type: Object,
        default: null,
      },
      pageNumbers: {
        type: Object,
        default: null,
      },
      lengths: {
        type: Object,
        default: null,
      },
      isSharedPlan: {
        type: Boolean,
        default: false,
      },
      isSharedPlanPreviewEnabled: {
        type: Boolean,
        default: false,
      },
      showExportSection: {
        type: Boolean,
        default: true,
      },
      areGeofencesCreatedLessThan5Min: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      ...mapGetters('orgApp', ['isSupplyOrganization']),
      ...mapState('orgApp', {
        organization: (state) => state.organization,
        organizationSettings: (state) => state.organizationSettings,
      }),
      targetedScreenCount() {
        return this.targetedUnitIDs.length;
      },
      targetedSiteCount() {
        return this.targetedSiteIDs.length;
      },
      targetedScreenCountFormatted() {
        return this.targetedScreenCount != null ? formatNumber(this.targetedScreenCount, '0,0') : DASH;
      },
      targetedSiteCountFormatted() {
        return this.targetedSiteCount != null ? formatNumber(this.targetedSiteCount, '0,0') : DASH;
      },
      physicalScreenCountFormatted() {
        return this.physicalScreenCount != null ? formatNumber(this.physicalScreenCount, '0,0') : DASH;
      },
      isLineItemOrPlan() {
        return this.entity instanceof LineItem || this.entity instanceof Plan;
      },
      isCreative() {
        return this.entity instanceof Creative;
      },
      isDeal() {
        return this.entity instanceof Package;
      },
      isCreativeMediaOwnerApproval() {
        return this.entity instanceof InventoryTargeting;
      },
      overriddenScreenCountTooltipContent() {
        return capitalize(
          i18n.global.t('messages.physicalScreensInfo', [
            this.targetedScreenCountFormatted,
            this.physicalScreenCountFormatted,
          ]),
        );
      },
      isDisplayVideoLineItem() {
        return (this.entity instanceof LineItem && this.entity?.isChannelTypeDisplayVideo) || false;
      },
    },
    methods: {
      capitalize,
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss">
  @use '@/styles/variables.scss' as *;

  #app {
    .main-content {
      .inventory-in-geofences {
        margin: 1rem 0 0 0;
        border-radius: 0.4rem;
        padding: 1.2rem 1.6rem;
        background: #eaeaea;

        color: var(--textMutedColor);

        h4 {
          margin: 0 0 1.2rem 0;
          display: flex;
          align-items: center;

          svg {
            fill: var(--textMutedColor);
            stroke: var(--textMutedColor);

            width: 1.6rem;
            height: 1.6rem;
            margin-right: 0.6rem;
          }
        }

        .inventory-in-geofences-actions {
          display: flex;

          button {
            box-shadow: 0 0px 10px rgba(0, 0, 0, 0.2);
          }
        }
      }
    }
  }

  #app .main-content .pane-header .kpis .kpi .number-and-text h4 {
    display: flex;
    align-items: center;

    .ml-2.badge {
      padding: 0;

      svg {
        width: 0.95rem;
        height: 0.95rem;
        stroke: var(--primaryColor);
      }
    }
  }
</style>
